import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  GET_RELEASE_QUERY_KEY,
  getRelease,
} from "../../reader/api/readerScreenApi";
import { readerDataStore } from "../../../hookstate/readerDataStore";
import { useSlideInMenu } from "../../../components/slideInMenu/hooks/useSlideInMenu";
import { SLIDE_IN_MENUS } from "../../root/constants";

/**
 * Open the existing slide-in CommentsMenu against a feed-supplied release.
 *
 * The CommentsMenu reads its release from the global readerDataStore
 * (via useReleaseData / useHydrateComments). It was built for the in-reader
 * flow where ReaderStateWrapper hydrates that store on mount, so opening it
 * from the feed requires us to hydrate manually first.
 *
 * Flow on click:
 *   1. Open the slide-in immediately so the user gets feedback. The menu's
 *      own loaders will spin until releaseData is populated.
 *   2. Fetch the release via React Query (same key the reader uses, so a
 *      subsequent navigation to the actual reader will hit the cache).
 *   3. Write the release into readerDataStore so CommentsMenu can render
 *      its comment list.
 *
 * Tradeoff: this WRITES to the global readerDataStore from a non-reader
 * context. If the user happens to be navigating into a different reader at
 * the same instant, the late-arriving fetch could overwrite their data.
 * Acceptable for the suggestion-feed prototype while we validate the UX;
 * a follow-up pass should refactor CommentsMenu to read its release from
 * the slide-in menu config so this hydration step is unnecessary.
 */
export function useOpenFeedCommentsMenu() {
  const queryClient = useQueryClient();
  const { onToggleSlideInMenu } = useSlideInMenu();

  return useCallback(
    (releaseUuid: string) => {
      if (!releaseUuid) return;

      // Open the slide-in first so taps feel instant. Pass the releaseUuid
      // through the menu config — CommentsMenu doesn't read it (it pulls
      // from readerDataStore), but other slide-ins do, so the value is
      // future-proofing for the eventual config-driven refactor.
      onToggleSlideInMenu(SLIDE_IN_MENUS.comments, { releaseUuid });

      void queryClient
        .fetchQuery({
          queryKey: [...GET_RELEASE_QUERY_KEY, releaseUuid],
          queryFn: () => getRelease(releaseUuid),
          staleTime: 5 * 60 * 1000,
        })
        .then((response) => {
          const release = response?.data?.payload?.results;
          if (release) {
            readerDataStore.releaseData.set(release);
          }
        })
        .catch(() => {
          // Swallow — the menu shows its own empty/error state when
          // releaseData stays unhydrated.
        });
    },
    [onToggleSlideInMenu, queryClient],
  );
}
